import { SUPPORTED_PHONE_COUNTRIES } from "../constants/Constants";

export const getExpectedPhoneCountry = (currency) =>
  SUPPORTED_PHONE_COUNTRIES[currency] || SUPPORTED_PHONE_COUNTRIES.AED;

export const phoneCountryIso = (currency) => getExpectedPhoneCountry(currency).iso;

export const supportedPhoneCountryIsoList = () =>
  Object.values(SUPPORTED_PHONE_COUNTRIES).map((c) => c.iso);

// Yup .test() validator: the entered number's dial code must match the
// vendor profile's own country (derived from currency), not just any
// supported country. Scales to however many entries exist in
// SUPPORTED_PHONE_COUNTRIES — no per-country branching needed here.
export const countryMatchProfileTest = (currency) =>
  function (value) {
    if (!value) return true;

    const expected = getExpectedPhoneCountry(currency);
    const cleanedValue = value.replace(/\D/g, "");

    const isOtherSupportedCountry = Object.values(SUPPORTED_PHONE_COUNTRIES).some(
      (country) =>
        country.dialCode !== expected.dialCode &&
        cleanedValue.startsWith(country.dialCode)
    );

    if (isOtherSupportedCountry) {
      return this.createError({
        message: `Please select a ${expected.name} number — this matches your profile's country.`,
      });
    }
    return true;
  };

// Yup .test() validator: the local number (after the dial code) must match
// the expected country's real mobile number pattern — not just be "long
// enough". Rejects things like 91-00XXXXXXXX or 91-1XXXXXXXXX, which the
// old length-only checks let through.
export const validMobileNumberTest = (currency) =>
  function (value) {
    if (!value) return true;

    const expected = getExpectedPhoneCountry(currency);
    const cleanedValue = value.replace(/\D/g, "");

    if (!cleanedValue.startsWith(expected.dialCode)) {
      // Wrong-country-dial-code case is reported by countryMatchProfileTest.
      return true;
    }

    const localNumber = cleanedValue.slice(expected.dialCode.length);
    if (!expected.localPattern.test(localNumber)) {
      return this.createError({
        message:
          expected.iso === "in"
            ? "Please enter a valid Indian mobile number (10 digits, starting with 6-9)."
            : "Please enter a valid UAE mobile number (9 digits, starting with 5).",
      });
    }
    return true;
  };

// Same pattern check as validMobileNumberTest, but determines the expected
// country from the number's OWN dial code instead of a fixed currency —
// for flows that allow entering a number from any supported country (e.g.
// "Utilstop" profile bookings), where the format must still be validated
// against whichever country was actually chosen.
export const validMobileFormatByOwnPrefixTest = () =>
  function (value) {
    if (!value) return true;

    const cleanedValue = value.replace(/\D/g, "");
    const matchedCountry = Object.values(SUPPORTED_PHONE_COUNTRIES).find((c) =>
      cleanedValue.startsWith(c.dialCode)
    );
    if (!matchedCountry) return true;

    const localNumber = cleanedValue.slice(matchedCountry.dialCode.length);
    if (!matchedCountry.localPattern.test(localNumber)) {
      return this.createError({
        message:
          matchedCountry.iso === "in"
            ? "Please enter a valid Indian mobile number (10 digits, starting with 6-9)."
            : "Please enter a valid UAE mobile number (9 digits, starting with 5).",
      });
    }
    return true;
  };
